import { useState, useEffect, useCallback } from 'react';
import { getSessionUsage, recordAskSalahSession } from 'src/api/askSalah';
import type { AskSalahSessionType, AskSalahSessionUsage } from 'src/types/askSalah';
import { useAskSalahSubscription } from './useAskSalahSubscription';

/**
 * Hook to manage session usage and limits
 * 
 * Returns:
 * - sessionsUsed: Current month's session count
 * - sessionsLimit: Monthly session limit
 * - sessionsRemaining: Calculated remaining sessions
 * - canStartSession: Boolean if user can start new session
 * - recordSession: Function to log session usage
 * - isTrialExhausted: Boolean if trial sessions consumed
 * - isLoading: Loading state
 * - error: Error state
 * - refetch: Function to manually refetch usage
 */
export const useAskSalahSession = () => {
  const { subscription, isActive, planType } = useAskSalahSubscription();
  const [usage, setUsage] = useState<AskSalahSessionUsage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch session usage
  const fetchUsage = useCallback(async () => {
    if (!isActive) {
      setUsage(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await getSessionUsage();
      setUsage(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch session usage');
      console.error('Error fetching session usage:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isActive]);

  // Fetch usage on mount and when subscription changes
  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Record a session
  const recordSession = useCallback(
    async (sessionType: AskSalahSessionType) => {
      try {
        setError(null);
        await recordAskSalahSession(sessionType);
        // Refetch usage after recording
        await fetchUsage();
        return { success: true };
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to record session';
        setError(errorMessage);
        console.error('Error recording session:', err);
        return { success: false, error: errorMessage };
      }
    },
    [fetchUsage]
  );

  const sessionsUsed = usage?.used || subscription?.sessionsUsedThisMonth || 0;
  const sessionsLimit = usage?.limit || subscription?.sessionsLimit || 0;
  const sessionsRemaining = Math.max(0, sessionsLimit - sessionsUsed);
  const canStartSession = isActive && sessionsRemaining > 0;
  const isTrialExhausted =
    String(planType || '').toLowerCase() === 'trial' &&
    sessionsUsed >= sessionsLimit;

  return {
    sessionsUsed,
    sessionsLimit,
    sessionsRemaining,
    canStartSession,
    recordSession,
    isTrialExhausted,
    isLoading,
    error,
    refetch: fetchUsage,
    resetDate: usage?.resetDate || null,
  };
};


